import type { CityConfig } from "../city-template/types";

export const augustaMeConfig: CityConfig = {
  city: "Augusta",
  state: "Maine",
  stateAbbr: "ME",
  slug: "augusta-me",
  route: "/cities/augusta-me",
  title: "Emergency Passport Augusta, ME — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Augusta, ME — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Kennebec County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Augusta, ME",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Augusta and Kennebec County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Augusta%20Maine%20state%20capital%20skyline%20featuring%20the%20iconic%20Maine%20State%20House%20with%20its%20copper%20dome%2C%20historic%20downtown%20along%20the%20Kennebec%20River%20waterfront%2C%20charming%20New%20England%20architecture%20with%20brick%20buildings%2C%20warm%20golden%20hour%20light%20over%20the%20river%20valley%2C%20autumn%20colors%20in%20the%20foreground%2C%20clean%20and%20inviting%20cityscape%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20warm%20earth%20tones%20and%20soft%20sky%2C%20photorealistic&width=1800&height=700&seq=augusta-me-hero-001&orientation=landscape",
  heroImageAlt: "Augusta Maine State House and downtown along the Kennebec River",
  geoPosition: "44.3106;-69.7795",
  geoPlacename: "Augusta, Maine",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "10 Causeway St Suite 247, Boston MA 02222",
    distance: "Approximately 165 miles, 2.5 hours via I-95",
  },
  nearbyCitiesZips: [
    { name: "Augusta", zips: "04330, 04332, 04333, 04336, 04338" },
    { name: "Hallowell", zips: "04347" },
    { name: "Gardiner", zips: "04345" },
    { name: "Winthrop", zips: "04364" },
    { name: "Manchester", zips: "04351" },
    { name: "Readfield", zips: "04355" },
    { name: "Belgrade", zips: "04917" },
    { name: "Sidney", zips: "04330" },
    { name: "Vassalboro", zips: "04989" },
    { name: "Chelsea", zips: "04330" },
    { name: "Farmingdale", zips: "04344" },
    { name: "Randolph", zips: "04346" },
    { name: "China", zips: "04358" },
    { name: "Winslow", zips: "04901" },
    { name: "Waterville", zips: "04901" },
    { name: "Oakland", zips: "04963" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Kennebec County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Portland", route: "/cities/portland-me" },
    { name: "Bangor", route: "/cities/bangor" },
    { name: "Lewiston", route: "/cities/lewiston" },
    { name: "Waterville", route: "/cities/waterville" },
    { name: "Brunswick", route: "/cities/brunswick" },
    { name: "Camden", route: "/cities/camden" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Augusta remotely via secure courier. We do not have a walk-in office in Augusta — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Kennebec County travelers.",
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeDistance: "Serving all of Kennebec County and Central Maine",
  serviceAreaIntro: "From downtown Augusta's Water Street corridor to the Maine State House complex, from the Hallowell waterfront to the Belgrade Lakes region — Fast Passport Center serves the entire Augusta community. We regularly help travelers from Gardiner, Winthrop, Vassalboro, and Waterville get their passports fast.",
  nearbyCitiesIntro: "We serve all Augusta neighborhoods and nearby Kennebec County communities including Hallowell, Gardiner, Winthrop, Manchester, Readfield, Belgrade, Sidney, Vassalboro, Chelsea, Farmingdale, Randolph, Winslow, Waterville, and Oakland. If you are in the greater Augusta area, we have you covered.",
  howItWorksIntro: "Most Augusta travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency.",
  mistakesIntro: "With Augusta State Airport (AUG) and easy access to Portland International Jetport, central Maine travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Augusta and central Maine travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Augusta? Here are the most common ones we hear from Maine travelers.",
  processingIntro: "Augusta travelers — state government officials with international obligations, Colby and UMA students heading abroad, healthcare professionals at MaineGeneral Medical Center attending global conferences, and Kennebec County families planning overseas vacations — have several options for getting a passport fast. Here is how they compare, and why most central Maine residents choose a U.S. Department of State registered passport courier for predictable, professional processing without the 2.5-hour drive to Boston.",
  localAreaIntro: [
    "Augusta, Maine is the state capital and the administrative center of the Pine Tree State. The Maine State House, with its distinctive copper dome designed by Charles Bulfinch, houses the Governor's office, the Maine Legislature, and dozens of state agencies. State employees — from Department of Economic and Community Development trade specialists to Department of Environmental Protection scientists attending international climate conferences — frequently travel abroad on government business. When a state trade delegation receives a last-minute invitation to an international summit, standard passport processing timelines simply do not work.",
    "The education sector is robust in the Augusta area. The University of Maine at Augusta (UMA) serves thousands of students across its campuses, many of whom participate in study abroad and international exchange programs. Colby College in nearby Waterville — one of Maine's most prestigious liberal arts colleges — sends hundreds of students abroad annually through programs spanning 60+ countries. When a Colby student receives their Jan Plan abroad acceptance in October for a January departure, the 10-week window requires expedited passport processing.",
    "MaineGeneral Medical Center, with its newly constructed Alfond Center for Health, is the region's largest employer and a source of international medical travel. Physicians attend conferences in Geneva, London, and Tokyo. Medical residents participate in global health rotations in Uganda, Haiti, and India. The life-or-death nature of healthcare means these travel plans emerge quickly and require fast passport turnaround.",
    "Augusta State Airport (AUG) provides regional air service, but most central Maine international travelers connect through Portland International Jetport (PWM) — about 60 miles south. The drive to PWM is manageable, but the drive to the Boston Passport Agency — 165 miles each way — is not something most central Maine residents want to make for a passport emergency. Fast Passport Center's courier service eliminates this entirely: complete your application online, ship documents overnight from any UPS Store in Augusta, and let our registered couriers handle the Boston agency submission."
  ],
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  dmvWebsite: "https://www.maine.gov/sos/bmv",
  dmvLabel: "Maine BMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Augusta international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Patricia W.",
      location: "Augusta, ME",
      rating: 5,
      text: "My business trip to Ottawa was in 3 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Absolutely saved my trip.",
      service: "Emergency Passport",
    },
    {
      name: "Thomas G.",
      location: "Hallowell, ME",
      rating: 5,
      text: "Needed to renew for a family vacation to Ireland. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Michelle R.",
      location: "Gardiner, ME",
      rating: 5,
      text: "Lost my passport the week before my trip to England. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Brian K.",
      location: "Winthrop, ME",
      rating: 5,
      text: "As a frequent international traveler from central Maine, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "augusta-me-1",
      name: "Augusta Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "40 Western Ave",
      city: "Augusta",
      state: "ME",
      zip: "04330",
      phone: "(207) 622-6177",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Augusta",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Augusta. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "augusta-me-2",
      name: "Gardiner Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "40 Bridge St",
      city: "Gardiner",
      state: "ME",
      zip: "04345",
      phone: "(207) 582-2130",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "6 miles south of Augusta",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Gardiner and Randolph residents. Limited hours — book appointments well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "augusta-me-3",
      name: "Waterville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "33 College Ave",
      city: "Waterville",
      state: "ME",
      zip: "04901",
      phone: "(207) 872-8511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "18 miles north of Augusta",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Waterville, Winslow, and Colby College area. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "augusta-me-4",
      name: "Winthrop Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "124 Main St",
      city: "Winthrop",
      state: "ME",
      zip: "04364",
      phone: "(207) 377-2200",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "10 miles west of Augusta",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Winthrop and Belgrade Lakes area. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "augusta-me-5",
      name: "Augusta City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "16 Cony St",
      city: "Augusta",
      state: "ME",
      zip: "04330",
      phone: "(207) 626-2310",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.2 miles from City Hall",
      website: "https://www.augustamaine.gov/",
      websiteLabel: "Augusta City Clerk",
      notes: "Major municipal facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 165 miles to Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "Weekend Availability", agency: "No — business hours only", fpc: "Yes — we work weekends for urgent cases" },
    { feature: "Photo Review", agency: "No — your problem if rejected", fpc: "Yes — pre-submission verification" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Maine photo shops often use generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Kennebec County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Augusta applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Augusta families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Augusta travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Augusta." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Augusta address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Augusta before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Augusta for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most central Maine travelers." },
    { q: "How fast can I renew my passport in Augusta?", a: "Fast Passport Center offers fast passport renewal in Augusta in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my trip to Canada?", a: "If your passport expires before your trip to Canada, contact Fast Passport Center immediately. We can expedite your passport renewal in Augusta in 24–48 hours so you don't miss your departure. Maine residents frequently cross into Quebec and New Brunswick — don't get caught at the border." },
    { q: "Can I get a same-day passport appointment near Augusta?", a: "Same-day passport appointments are only available at the Boston Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Augusta residents — without the 2.5-hour drive to Boston." },
    { q: "Can I expedite a child passport in Augusta?", a: "Yes. Fast Passport Center expedites child passports in Augusta for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Augusta with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Augusta?", a: "With Fast Passport Center's emergency courier service, Augusta travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to visit the Boston Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Augusta?", a: "Popular options include the Augusta Main Post Office (04330), Gardiner Post Office (04345), Waterville Post Office (04901), and Augusta City Clerk (04330). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Augusta?", a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option." },
    { q: "What if I live in a nearby town like Gardiner or Winthrop?", a: "We serve all of Kennebec County including Hallowell, Gardiner, Winthrop, Manchester, Readfield, Belgrade, Sidney, Vassalboro, Winslow, Waterville, and Oakland. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Augusta?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Augusta", address: "10 Whitten Rd, Augusta, ME 04330", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Augusta", address: "403 Water St, Augusta, ME 04330", hours: "Mon–Sun: 7AM–11PM" },
    { name: "UPS Store — Augusta", address: "56 Western Ave, Augusta, ME 04330", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Augusta", address: "80 Stephen King Dr, Augusta, ME 04330", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Gardiner", address: "40 Maine Ave, Gardiner, ME 04345", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Waterville", address: "325 Main St, Waterville, ME 04901", hours: "Mon–Sun: 7AM–10PM" },
  ],
  dmvLocations: [
    { name: "BMV — Augusta", address: "19 Anthony Ave, Augusta, ME 04333", hours: "Mon–Fri: 8AM–4:30PM", note: "REAL ID and driver's license renewals. Appointment required.", featured: true },
    { name: "BMV — Waterville", address: "46 Main St, Waterville, ME 04901", hours: "Mon–Fri: 8AM–4:30PM", note: "Passport-ready photo services available. Online appointment booking.", featured: false },
    { name: "BMV — Rockland", address: "212 New County Rd, Rockland, ME 04841", hours: "Mon–Fri: 8AM–4:30PM", note: "Midcoast Maine location. Walk-ins limited — book online first.", featured: false },
  ],
  airports: [
    { code: "AUG", name: "Augusta State Airport", distance: "2 miles from downtown Augusta", address: "75 Airport Rd, Augusta, ME 04330", note: "Regional airport with connections to Boston Logan. General aviation and Cape Air service available.", flightUrl: "https://www.augustamaine.gov/", flightLabel: "Augusta Airport", featured: true },
    { code: "PWM", name: "Portland International Jetport", distance: "60 miles from Augusta", address: "1001 Westbrook St, Portland, ME 04102", note: "Maine's busiest airport with direct flights to major hubs and seasonal international service. Primary gateway for central Maine travelers.", flightUrl: "https://www.portlandjetport.org/", flightLabel: "PWM Flight Info", featured: false },
    { code: "BOS", name: "Boston Logan International Airport", distance: "165 miles from Augusta", address: "1 Harborside Dr, Boston, MA 02128", note: "Major international hub for long-haul flights to Europe, Asia, and South America. Accessible via I-95.", flightUrl: "https://www.massport.com/logan-airport", flightLabel: "Logan Airport", featured: false },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from Maine CDC Vital Records in Augusta. Online, mail, or in-person options available for Kennebec County residents.", url: "https://www.maine.gov/dhhs/mecdc/public-health-systems/data-research/vital-records/" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through Maine CDC Vital Records. Required for estate, insurance, and legal matters.", url: "https://www.maine.gov/dhhs/mecdc/public-health-systems/data-research/vital-records/" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Augusta City Clerk at 16 Cony Street. Certified copies available for name changes and legal purposes.", url: "https://www.augustamaine.gov/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Kennebec County Superior Court. Required for name change passport corrections.", url: "https://www.courts.maine.gov/" },
  ],
};